import { formatTons, getAssignmentTripCount } from "./resultsFleetData.js";

export { getAssignmentTripCount };

const TRANSIT_MONTH_LABELS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];

// « 29 sept. », « 1er oct. » : même libellé que le refus de réactivation côté serveur.
function formatTransitDateLabel(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(typeof value === "string" ? value : "");
  if (!match) {
    return "?";
  }
  const day = Number(match[3]);
  return `${day === 1 ? "1er" : day} ${TRANSIT_MONTH_LABELS[Number(match[2]) - 1] || ""}`.trim();
}

// Remplace « Réactiver dans le dispatch » tant que le Chargé PM existe (refus 409 côté serveur).
export function resolveEndOfDayLoadTransitReactivationMessage(assignment) {
  if (!assignment?.is_dispatch_disabled || assignment?.dispatch_disabled_reason !== "end_of_day_load_transit") {
    return "";
  }
  return `Camion chargé en PM (${formatTransitDateLabel(assignment.dispatch_disabled_source_dispatch_date)} → ${formatTransitDateLabel(assignment.dispatch_disabled_target_dispatch_date)}). Modifiez ou retirez le Chargé PM pour le rendre disponible.`;
}

function addFleetSearchValue(values, value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    values.add(String(value));
    return;
  }

  if (typeof value !== "string") {
    return;
  }

  const trimmedValue = value.trim();
  if (trimmedValue) {
    values.add(trimmedValue);
  }
}

function addFleetSearchObjectValues(values, source, keys = []) {
  if (!source || typeof source !== "object") {
    return;
  }

  keys.forEach((key) => {
    addFleetSearchValue(values, source[key]);
  });
}

function addFleetSearchAddressValues(values, address) {
  if (!address) {
    return;
  }

  if (typeof address === "string") {
    addFleetSearchValue(values, address);
    return;
  }

  addFleetSearchObjectValues(values, address, [
    "city",
    "fullAddress",
    "full_address",
    "label",
    "line1",
    "line2",
    "name",
    "postalCode",
    "postal_code",
    "street",
  ]);
}

function addFleetSearchOrderValues(values, item) {
  if (!item || typeof item !== "object") {
    return;
  }

  addFleetSearchObjectValues(values, item, [
    "client_code",
    "client_name",
    "delivery_id",
    "grain_code",
    "grain_type",
    "label",
    "loading_location_name",
    "order_id",
    "product_code",
    "product_name",
    "site_name",
    "step_id",
    "work_order_number",
    "workOrderNumber",
    "workOrderLabel",
  ]);
  addFleetSearchAddressValues(values, item.address);
}

function addFleetSearchTripValues(values, trip) {
  if (!trip || typeof trip !== "object") {
    return;
  }

  addFleetSearchObjectValues(values, trip, ["trip_id"]);

  (Array.isArray(trip.clients) ? trip.clients : []).forEach((client) => {
    addFleetSearchOrderValues(values, client);
  });

  (Array.isArray(trip.operational_steps) ? trip.operational_steps : []).forEach((step) => {
    addFleetSearchOrderValues(values, step);
  });

  (Array.isArray(trip.orders) ? trip.orders : []).forEach((order) => {
    addFleetSearchOrderValues(values, order);
  });

  (Array.isArray(trip.routable_stops) ? trip.routable_stops : []).forEach((stop) => {
    addFleetSearchOrderValues(values, stop);
  });
}

function addFleetSearchEndOfDayLoadValues(values, marker) {
  if (!marker || typeof marker !== "object") {
    return;
  }

  addFleetSearchObjectValues(values, marker, [
    "marker_key",
    "end_of_day_load_marker_key",
    "loading_label",
    "target_truck_id",
    "source_truck_id",
  ]);

  (Array.isArray(marker.order_ids) ? marker.order_ids : []).forEach((orderId) => {
    addFleetSearchValue(values, orderId);
  });
  (Array.isArray(marker.delivery_ids) ? marker.delivery_ids : []).forEach((deliveryId) => {
    addFleetSearchValue(values, deliveryId);
  });

  (Array.isArray(marker.items) ? marker.items : []).forEach((item) => {
    addFleetSearchOrderValues(values, item);
    addFleetSearchObjectValues(values, item, [
      "loading_client_name",
      "loading_address",
      "work_order_number",
      "workOrderNumber",
      "workOrderLabel",
    ]);
    addFleetSearchAddressValues(values, item?.loading_address);
    addFleetSearchAddressValues(values, item?.address);
  });

  (Array.isArray(marker.target_suspended_deliveries) ? marker.target_suspended_deliveries : []).forEach((item) => {
    addFleetSearchOrderValues(values, item);
  });
}

export function buildFleetSearchValue(assignment) {
  const values = new Set();

  addFleetSearchObjectValues(values, assignment, [
    "aspirator",
    "driver_name",
    "locked_grain_type",
    "truck_id",
    "truck_name",
  ]);
  if (assignment?.is_dispatch_disabled) {
    addFleetSearchValue(values, "hors dispatch");
    addFleetSearchValue(values, "dispatch désactivé");
  }
  addFleetSearchValue(values, assignment?.axle_count ? `${assignment.axle_count} essieux` : "");
  addFleetSearchAddressValues(values, assignment?.address);

  (Array.isArray(assignment?.operational_steps) ? assignment.operational_steps : []).forEach((step) => {
    addFleetSearchOrderValues(values, step);
  });

  (Array.isArray(assignment?.trips) ? assignment.trips : []).forEach((trip) => {
    addFleetSearchTripValues(values, trip);
  });

  (Array.isArray(assignment?.end_of_day_loads) ? assignment.end_of_day_loads : []).forEach((marker) => {
    addFleetSearchEndOfDayLoadValues(values, marker);
  });
  (Array.isArray(assignment?.end_of_day_load_source_preloads) ? assignment.end_of_day_load_source_preloads : []).forEach((marker) => {
    addFleetSearchEndOfDayLoadValues(values, marker);
  });

  return Array.from(values).join(" ").trim();
}

function addSuspendedPanelItemSearchValues(values, item) {
  if (!item || typeof item !== "object") {
    return;
  }

  addFleetSearchObjectValues(values, item, [
    "badgeLabel",
    "buildingLabel",
    "currentTruckId",
    "deliveryAddress",
    "deliveryId",
    "deliveryName",
    "loadingAddress",
    "loadingName",
    "meta",
    "orderLabel",
    "productLabel",
    "siloLabel",
    "subtitle",
    "suspendedGroupLabel",
    "title",
    "workOrderLabel",
  ]);

  (Array.isArray(item.orderIds) ? item.orderIds : []).forEach((orderId) => {
    addFleetSearchValue(values, orderId);
  });
  (Array.isArray(item.deliveryIds) ? item.deliveryIds : []).forEach((deliveryId) => {
    addFleetSearchValue(values, deliveryId);
  });
}

export function buildSuspendedSearchValue(item) {
  const values = new Set();

  addSuspendedPanelItemSearchValues(values, item);

  (Array.isArray(item?.memberItems) ? item.memberItems : []).forEach((memberItem) => {
    addSuspendedPanelItemSearchValues(values, memberItem);
  });

  return Array.from(values).join(" ").trim();
}

function getAssignmentStatusMeta(assignment) {
  const trips = Array.isArray(assignment?.trips) ? assignment.trips : [];
  const tripCount = getAssignmentTripCount(assignment);
  const successRoutes = trips.filter((trip) => trip?.route?.status === "success").length;
  const errorRoutes = trips.filter((trip) => trip?.route?.status === "error").length;

  if (!tripCount) {
    return { label: "Sans tournée", tone: "danger" };
  }

  if (errorRoutes > 0) {
    return { label: "Routage partiel", tone: "warning" };
  }

  if (successRoutes === tripCount) {
    return { label: "Routage calculé", tone: "success" };
  }

  return { label: "Heuristique seule", tone: "neutral" };
}

export function computeAssignmentApprovalInfo(operationalSteps) {
  const steps = Array.isArray(operationalSteps) ? operationalSteps.filter(Boolean) : [];
  const seenOrders = new Set();
  let approved = 0;
  let total = 0;

  steps.forEach((step) => {
    if (!step?.order_id || seenOrders.has(step.order_id)) {
      return;
    }

    seenOrders.add(step.order_id);
    total += 1;

    const loadingOk = steps.some((candidate) => (
      candidate?.order_id === step.order_id
      && candidate?.step_type === "loading"
      && candidate?.is_confirmed
    ));
    const deliveryOk = steps.some((candidate) => (
      candidate?.order_id === step.order_id
      && candidate?.step_type === "delivery"
      && candidate?.is_confirmed
    ));

    if (loadingOk && deliveryOk) {
      approved += 1;
    }
  });

  return { approved, total };
}

// « Protéger / Déprotéger les commandes » laisse les commandes gelées ou livrées telles quelles : elles ne comptent pas.
// Filtre par commande : l'étape de chargement d'une commande gelée n'est pas toujours marquée.
export function computeProtectableOrderApprovalInfo(operationalSteps) {
  const steps = Array.isArray(operationalSteps) ? operationalSteps.filter(Boolean) : [];
  const lockedOrderIds = new Set(steps
    .filter((step) => step.is_dispatcher_frozen || step.is_dispatcher_delivered)
    .map((step) => step.order_id));
  return computeAssignmentApprovalInfo(steps.filter((step) => !lockedOrderIds.has(step.order_id)));
}

// « Camion à choisir » : la ligne prend le nom de sa remorque (« 1906-W27 » devient « W27 ») ; le camion habituel reste
// dans l'infobulle de la puce. Les autres lignes gardent le nom du plan.
function resolveMissingTractorName(assignment) {
  const tractorName = String(assignment?.day_truck_name || "").trim();
  return assignment?.tractor_status === "missing" && tractorName && String(assignment?.truck_name || "").startsWith(`${tractorName}-`)
    ? tractorName : "";
}

export function resolveAssignmentDisplayName(assignment) {
  const tractorName = resolveMissingTractorName(assignment);
  return tractorName ? assignment.truck_name.slice(tractorName.length + 1) : assignment?.truck_name || assignment?.truck_id || "";
}

export function resolveMissingTractorTitle(assignment) {
  const tractorName = resolveMissingTractorName(assignment);
  return tractorName
    ? `Camion habituel : ${tractorName} (il ne tire pas cette remorque ce jour-là). Choisissez un camion : « Changer de camion ».`
    : "Remorque sans camion ce jour-là. Choisissez un camion : « Changer de camion ».";
}

function getAssignmentGrainType(operationalSteps, assignment) {
  const steps = Array.isArray(operationalSteps) ? operationalSteps.filter(Boolean) : [];
  const stepWithGrainType = steps.find((step) => typeof step?.grain_type === "string" && step.grain_type.trim());

  if (stepWithGrainType?.grain_type) {
    return stepWithGrainType.grain_type;
  }

  return typeof assignment?.locked_grain_type === "string" ? assignment.locked_grain_type : "";
}

export function buildAssignmentShellViewModel({
  assignment,
  assignmentId,
  distanceSummary,
  timelineRenderState = null,
}) {
  const operationalSteps = Array.isArray(assignment?.operational_steps)
    ? assignment.operational_steps.filter(Boolean)
    : [];
  const suppressResidualProductBadges = timelineRenderState?.status === "empty";
  const isDispatchDisabled = Boolean(assignment?.is_dispatch_disabled);
  const isTruckProtected = Boolean(assignment?.is_truck_protected);
  const statusBadge = getAssignmentStatusMeta(assignment);
  const totalCompartments = Number.isFinite(assignment?.total_compartments) ? assignment.total_compartments : 0;
  const usedCompartments = Number.isFinite(assignment?.compartments_used) ? assignment.compartments_used : 0;
  const peakLoadTons = Number.isFinite(assignment?.peak_trip_load_tons) ? assignment.peak_trip_load_tons : 0;
  const manualMixGrainTypes = Array.isArray(assignment?.manual_mix_grain_types)
    ? assignment.manual_mix_grain_types.filter((value) => typeof value === "string" && value.trim())
    : [];
  const manualMixBadge = !suppressResidualProductBadges && assignment?.is_manual_mix
    ? {
      label: manualMixGrainTypes.length ? manualMixGrainTypes.join(", ") : "",
      products: manualMixGrainTypes,
      title: manualMixGrainTypes.length ? manualMixGrainTypes.join(", ") : "",
    }
    : null;
  const grainTypeLabel = suppressResidualProductBadges || assignment?.is_manual_mix
    ? ""
    : getAssignmentGrainType(operationalSteps, assignment);
  const chargedPmProductLabels = suppressResidualProductBadges || assignment?.is_manual_mix
    ? []
    : [...new Set((Array.isArray(assignment?.end_of_day_loads) ? assignment.end_of_day_loads : [])
      .flatMap((marker) => (Array.isArray(marker?.grain_types) ? marker.grain_types : []))
      .map((value) => (typeof value === "string" ? value.trim() : ""))
      .filter(Boolean))];
  const shouldHideUnassignedStatusBadge = isDispatchDisabled && statusBadge.label === "Sans tournée";

  return {
    capacityLabel: `${formatTons(assignment?.capacity_tons)} t`,
    compartmentsLabel: `${usedCompartments}/${totalCompartments}`,
    distanceSummary: distanceSummary || assignment?.total_estimated_distance_label || "Distance estimée indisponible",
    grainTypeLabel,
    chargedPmProductLabels,
    peakLoadLabel: `${formatTons(peakLoadTons)} t`,
    totalCompartments,
    usedCompartments,
    showCompartments: totalCompartments > 0,
    suppressResidualProductBadges,
    statusBadge: {
      ...statusBadge,
      isHidden: statusBadge.label === "Routage calculé" || shouldHideUnassignedStatusBadge,
    },
    manualMixBadge,
    dispatchDisabledBadge: isDispatchDisabled
      ? assignment?.dispatch_disabled_reason === "end_of_day_load_transit"
        ? {
          label: "Hors dispatch · Chargé PM",
          title: `Chargé en PM le ${formatTransitDateLabel(assignment.dispatch_disabled_source_dispatch_date)} pour livraison le ${formatTransitDateLabel(assignment.dispatch_disabled_target_dispatch_date)}`
            .replace(/\.?$/, ". Non disponible ce jour."),
          isEndOfDayLoadTransit: true,
        }
        : {
          label: "Hors dispatch",
          title: "Camion désactivé pour ce dispatch. Il reste visible dans la flotte mais ne peut plus recevoir de réaffectation ni participer à Optimiser tant qu'il n'est pas réactivé.",
        }
      : null,
    truckProtectionBadge: isTruckProtected
      ? {
        label: "Protégé",
        title: "Camion protégé pour Réaffecter et Optimiser",
      }
      : null,
    tripCountLabel: String(getAssignmentTripCount(assignment)),
    truckTitle: resolveAssignmentDisplayName(assignment) || assignmentId || "Camion",
  };
}

export function resolveTruckStepProductLabels({ assignment, shell } = {}) {
  if (shell?.suppressResidualProductBadges === true) {
    return [];
  }

  const manualMixProducts = Array.isArray(shell?.manualMixBadge?.products)
    ? shell.manualMixBadge.products
      .map((value) => (typeof value === "string" ? value.trim() : ""))
      .filter(Boolean)
    : [];
  if (manualMixProducts.length > 1) {
    return manualMixProducts;
  }

  const operationalSteps = Array.isArray(assignment?.operational_steps)
    ? assignment.operational_steps
    : [];
  return [...new Set(
    operationalSteps
      .map((step) => (typeof step?.grain_type === "string" ? step.grain_type.trim() : ""))
      .filter(Boolean),
  )];
}

export function shouldShowTruckStepProductPills(productLabels) {
  return Array.isArray(productLabels) && productLabels.length > 1;
}
